import React, { useState, useEffect } from "react";
import { Button, Popover, PopoverTrigger, PopoverContent, PopoverBody } from "@chakra-ui/react";

import { SingleDatePicker } from "./components/SingleDatePicker";
import { formatDateAsLocaleDateString } from "@/utils/dateTimeUtils";
import { colours } from "@/theme/colours";
import { ChakraFAIcon } from "../ui/Icon";

export interface Props {
	value: Date | null;
	onDateChange: (date: Date | null) => void;
	dataTestId?: string;
	width?: string;
	isInvalid?: boolean;
}

/**
 * A date picker input that can be used to select a date.
 *
 * @example
 * ```tsx
 * // With a selected date
 * <DatePickerInput value={new Date()} onDateChange={(date) => {}} />
 *
 * // With no date selected (shows placeholder)
 * <DatePickerInput value={null} onDateChange={(date) => {}} />
 * ```
 */
export function DatePickerInput(props: Props): JSX.Element {
	const [date, setDate] = useState(props.value);

	// Sync internal state with props when they change
	useEffect(() => {
		setDate(props.value);
	}, [props.value]);

	const onSelect = (date: Date) => {
		setDate(date);
		props.onDateChange(date);
	};

	return (
		<Popover placement="bottom-start">
			{({ onClose }) => (
				<>
					<PopoverTrigger>
						<Button
							borderRadius={"md"}
							py={6}
							px={4}
							border={"1px solid"}
							borderColor={props.isInvalid ? colours.accent.red[200] : colours.neutrals[50]}
							background={"white"}
							fontWeight={400}
							color={props.isInvalid ? colours.accent.red[400] : date ? colours.neutrals[800] : colours.neutrals[400]}
							_hover={{
								background: colours.neutrals[15],
							}}
							width={props.width ?? "100%"}
							justifyContent="flex-start"
							data-testid={props.dataTestId}
						>
							<ChakraFAIcon
								icon={`fa-calendar-days`}
								variant="solid"
								color={props.isInvalid ? colours.accent.red[400] : colours.neutrals[200]}
								mr={3}
								boxSize={4}
								mb={0.5}
							/>
							{date ? formatDateAsLocaleDateString(date) : "Enter a date"}
						</Button>
					</PopoverTrigger>
					<PopoverContent width="auto" border="1px solid" borderColor="gray.200" boxShadow="lg">
						<PopoverBody p={0}>
							<SingleDatePicker
								initialStartDate={date}
								onSelect={(selectedDate) => {
									onSelect(selectedDate);
									onClose();
								}}
							/>
						</PopoverBody>
					</PopoverContent>
				</>
			)}
		</Popover>
	);
}
